// THE ACCENT — one colour the operator may choose, and the maths that keeps ATTEST's contrast
// law when they do. Shared by the server (the gate on the write path, and the stylesheet
// /theme.css emits at boot) and by the Admin page (the live preview), so the ratio the
// operator sees IS the ratio the server judges.
//
// ATTEST (attest.css, "interaction — ONE accent"): --act must read ≥ 4.5:1 on the field and
// on the sheet (a link on paper), and --act-ink ≥ 4.5:1 on solid --act (text on a filled
// control). Night derives its own act — the same hue lifted until it clears ≥ 8:1 on the night
// field, where ATTEST's own sits at 8.9 — so a colour chosen for day never fails at night.

export const ATTEST_DEFAULT_ACT = '#2445C9';
export const AA = 4.5;
const DAY = { field: '#F4F2EE', sheet: '#FFFFFF', ink: '#1A1917', white: '#FFFFFF' };
const NIGHT = { field: '#14130F', sheet: '#1C1A16', ink: '#14130F' };

export const isHex = (v: string): boolean => /^#[0-9a-fA-F]{6}$/.test(v);

type RGB = [number, number, number];
const toRgb = (hex: string): RGB => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as RGB;
const toHex = (c: RGB): string =>
  `#${c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
const lin = (v: number): number => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
const luminance = (c: RGB): number => 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2]);

/** WCAG 2 contrast ratio, 1…21. */
export function contrast(a: string, b: string): number {
  const la = luminance(toRgb(a)), lb = luminance(toRgb(b));
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}
const mix = (a: string, b: string, t: number): string => {
  const A = toRgb(a), B = toRgb(b);
  return toHex([0, 1, 2].map((i) => A[i] + (B[i] - A[i]) * t) as RGB);
};

// HSL — for lightness moves that keep the hue
function toHsl(hex: string): [number, number, number] {
  const [r, g, b] = toRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}
function fromHsl(h: number, s: number, l: number): string {
  const f = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  if (s === 0) return toHex([l * 255, l * 255, l * 255]);
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
  return toHex([f(p, q, h + 1 / 3) * 255, f(p, q, h) * 255, f(p, q, h - 1 / 3) * 255]);
}
const withL = (hex: string, l: number): string => { const [h, s] = toHsl(hex); return fromHsl(h, s, Math.max(0, Math.min(1, l))); };

export interface AccentRamp { act: string; hover: string; soft: string; line: string; ink: string }
export interface AccentReport {
  hex: string;
  day: AccentRamp;
  night: AccentRamp;
  ratios: { dayField: number; daySheet: number; dayInk: number; nightField: number; nightSheet: number; nightInk: number };
  /** Empty means the colour passes and may be stored. */
  problems: string[];
}
const r2 = (n: number): string => `${n.toFixed(2)}:1`;

/** The full ramp for a candidate accent, both themes, with the ratios ATTEST measures. */
export function deriveAccent(hex: string): AccentReport {
  const act = hex.toUpperCase();
  const [, , l] = toHsl(act);
  const dayInk = contrast(DAY.white, act) >= contrast(DAY.ink, act) ? DAY.white : DAY.ink;
  const day: AccentRamp = {
    act,
    hover: withL(act, l - 0.09),
    soft: mix(act, DAY.sheet, 0.90),
    line: mix(act, DAY.sheet, 0.55),
    ink: dayInk,
  };
  let nl = Math.max(l, 0.55);
  let nact = withL(act, nl);
  while (contrast(nact, NIGHT.field) < 8 && nl < 0.92) { nl += 0.02; nact = withL(act, nl); }
  const night: AccentRamp = {
    act: nact,
    hover: withL(act, Math.min(0.96, nl + 0.08)),
    soft: mix(nact, NIGHT.sheet, 0.86),
    line: mix(nact, NIGHT.field, 0.50),
    ink: NIGHT.ink,
  };
  const ratios = {
    dayField: contrast(act, DAY.field),
    daySheet: contrast(act, DAY.sheet),
    dayInk: contrast(dayInk, act),
    nightField: contrast(nact, NIGHT.field),
    nightSheet: contrast(nact, NIGHT.sheet),
    nightInk: contrast(NIGHT.ink, nact),
  };
  const problems: string[] = [];
  if (ratios.dayField < AA) problems.push(`a link on the day field reads ${r2(ratios.dayField)} — needs ${AA}:1`);
  if (ratios.daySheet < AA) problems.push(`a link on the day sheet reads ${r2(ratios.daySheet)} — needs ${AA}:1`);
  if (ratios.dayInk < AA) problems.push(`text on a filled control reads ${r2(ratios.dayInk)} — needs ${AA}:1`);
  if (ratios.nightField < AA) problems.push(`the night act reads ${r2(ratios.nightField)} on the night field — needs ${AA}:1`);
  if (ratios.nightInk < AA) problems.push(`night text on a filled control reads ${r2(ratios.nightInk)} — needs ${AA}:1`);
  return { hex: act, day, night, ratios, problems };
}

/**
 * The nearest shade of a failing colour that passes — the SAME hue and saturation, darkened
 * one lightness step at a time until every ratio clears. Null when no shade of that hue can
 * (it always can, short of black; the bound is a guard).
 */
export function nearestPassing(hex: string): string | null {
  const [h, s, l] = toHsl(hex.toUpperCase());
  for (let L = l; L > 0.04; L -= 0.01) {
    const cand = fromHsl(h, s, L);
    if (!deriveAccent(cand).problems.length) return cand;
  }
  return null;
}

/** The stylesheet /theme.css serves: nothing when the accent is ATTEST's own or unfit. */
export function accentCss(hex: string | null | undefined): string {
  const v = (hex ?? '').trim();
  if (!v || !isHex(v) || v.toUpperCase() === ATTEST_DEFAULT_ACT) return "/* the accent is ATTEST's own */\n";
  const rep = deriveAccent(v);
  if (rep.problems.length) return `/* the stored accent ${v} fails AA — ATTEST's own stays in force */\n`;
  const block = (r: AccentRamp) =>
    `--act: ${r.act}; --act-hover: ${r.hover}; --act-soft: ${r.soft}; --act-line: ${r.line}; --act-ink: ${r.ink};`;
  return [
    `/* the operator's accent ${rep.hex} — ramp derived by src/shared/accent.ts, AA-gated on the write path */`,
    `:root, [data-theme="light"] { ${block(rep.day)} }`,
    `[data-theme="dark"] { ${block(rep.night)} }`,
    '',
  ].join('\n');
}
